import React from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { ACTION_PLAN_TAB_ALL } from 'components/ActionPlans/constants';
import { ActionPlanCompanyAttentionCommunityType } from 'types/actionPlans';

const Column = styled.div`
  flex: 1;
  min-width: 0;
  padding: 0 24px;
`;

const ColumnHeader = styled.div`
  display: flex;
  flex-flow: row;
  align-items: baseline;
  gap: 10px;
  padding-top: 20px;
`;

const ColumnTitle = styled.div`
  font-family: var(--font-bold);
  font-size: 14px;
`;

const NudgeList = styled.div`
  margin-top: 10px;
  padding-bottom: 30px;
`;

const NudgeRow = styled.a`
  display: flex;
  flex-flow: row;
  gap: 15px;
  padding: 10px 0;
  border-bottom: solid 1px var(--line-gray);
  text-decoration: none;
  cursor: pointer;

  &:hover .nudge-community-name {
    text-decoration: underline;
  }
`;

const NudgeAccent = styled.div`
  width: 4px;
  border-radius: 4px;
  background: linear-gradient(to bottom, var(--amber-light), var(--inactive-orange));
`;

const NudgeContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const CommunityName = styled.span`
  font-size: 13px;
  color: var(--dark-blue);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NudgeSummary = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
  flex-shrink: 0;
`;

const EmptyMessage = styled.div`
  color: var(--text-secondary);
  font-size: 12px;
  padding: 16px 0;
`;

type Props = {
  communities: ActionPlanCompanyAttentionCommunityType[];
};

const communityAllPlansPath = (communityId: number) =>
  `/community/${communityId}/action-plans?tab=${ACTION_PLAN_TAB_ALL}`;

const buildAttentionSummary = (community: ActionPlanCompanyAttentionCommunityType) => {
  const parts: string[] = [];
  if (community.overdue_count > 0) {
    parts.push(`${community.overdue_count} overdue`);
  }
  if (community.unassigned_count > 0) {
    parts.push(`${community.unassigned_count} unassigned`);
  }
  return parts.join(', ');
};

const NeedsANudgeColumn = ({ communities }: Props) => {
  const navigate = useNavigate();

  const goTo = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(path);
  };

  return (
    <Column data-test-id="action-plans-portfolio-nudge-column">
      <ColumnHeader>
        <ColumnTitle>Needs a Nudge</ColumnTitle>
      </ColumnHeader>
      <NudgeList>
        {communities.length === 0 && (
          <EmptyMessage data-test-id="action-plans-portfolio-nudge-empty">No communities need a nudge.</EmptyMessage>
        )}
        {communities.map((community) => {
          const actionPlansPath = communityAllPlansPath(community.id);

          return (
            <NudgeRow
              key={community.id}
              href={actionPlansPath}
              onClick={goTo(actionPlansPath)}
              data-test-id={`action-plans-portfolio-nudge-row-${community.id}`}
            >
              <NudgeAccent />
              <NudgeContent>
                <CommunityName className="nudge-community-name">{community.name}</CommunityName>
                <NudgeSummary>{buildAttentionSummary(community)}</NudgeSummary>
              </NudgeContent>
            </NudgeRow>
          );
        })}
      </NudgeList>
    </Column>
  );
};

export default NeedsANudgeColumn;
